import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, ArrowUpRight } from "lucide-react";
import { CONTACT } from "@/lib/api";
import { useT } from "@/lib/i18n";
import Bolt from "@/components/Bolt";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_europe-trade-hub/artifacts/wkf5kbul_Zopran%20Logo%20Rundausschnitt%20%281%29.png";

export default function Footer() {
  const { t } = useT();
  return (
    <footer
      data-testid="site-footer"
      className="relative zp-band-navy mt-24 overflow-hidden"
    >
      <div className="absolute inset-0 zp-grid-bg opacity-[0.18] pointer-events-none" />
      <Bolt
        size={520}
        color="#5CE1E6"
        className="absolute -right-24 -bottom-24 pointer-events-none"
        style={{ opacity: 0.06, transform: "rotate(-12deg)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 text-white">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-full overflow-hidden ring-1 ring-white/20 bg-white">
                <img src={LOGO_URL} alt="Zopran" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-display text-lg tracking-tight text-white">
                  <span className="zp-cyan-text">ZOPRAN</span>{" "}
                  <span className="text-white">Global Logistics Group</span>
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#A8C8D6] mt-1 font-semibold">
                  {t("foot.tagline")}
                </div>
              </div>
            </div>
            <p className="text-[#C9DCE4] max-w-md leading-relaxed text-sm">
              {t("foot.brand")}
            </p>

            <div className="mt-8 space-y-3 text-sm">
              <a
                data-testid="footer-email"
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-3 text-[#E2EEF3] hover:text-white"
              >
                <Mail size={16} className="text-[#5CE1E6]" /> {CONTACT.email}
              </a>
              <a
                data-testid="footer-phone"
                href={`tel:${CONTACT.phone}`}
                className="flex items-center gap-3 text-[#E2EEF3] hover:text-white"
              >
                <Phone size={16} className="text-[#5CE1E6]" /> {CONTACT.phone}
              </a>
              <div className="flex items-start gap-3 text-[#E2EEF3]">
                <MapPin size={16} className="text-[#5CE1E6] mt-0.5" />
                <span>{CONTACT.address}</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="zp-label mb-5 !text-[#5CE1E6]">{t("foot.brands")}</div>
            <ul className="space-y-3">
              {CONTACT.brands.map((b) => (
                <li key={b.url}>
                  <a
                    data-testid={`footer-brand-${b.name}`}
                    href={b.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group flex items-center justify-between text-[#E2EEF3] hover:text-white"
                  >
                    <span>{b.name}</span>
                    <ArrowUpRight
                      size={14}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition"
                    />
                  </a>
                </li>
              ))}
            </ul>

            <div className="zp-label mt-10 mb-5 !text-[#5CE1E6]">{t("foot.connect")}</div>
            <a
              data-testid="footer-linkedin"
              href={CONTACT.linkedin}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-xs uppercase tracking-[0.18em] hover:bg-white/15"
            >
              <Linkedin size={14} /> LinkedIn
            </a>
          </div>

          <div className="md:col-span-4">
            <div className="zp-label mb-5 !text-[#5CE1E6]">{t("foot.navigate")}</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                [t("nav.home"), "/"],
                [t("nav.services"), "/services"],
                [t("nav.industries"), "/industries"],
                [t("nav.about"), "/about"],
                [t("nav.partner"), "/partner"],
                [t("nav.contact"), "/contact"],
                [t("nav.quote"), "/quote"],
                [t("nav.impressum"), "/impressum"],
              ].map(([l, to]) => (
                <Link key={to} to={to} className="text-[#C9DCE4] hover:text-white py-1">
                  {l}
                </Link>
              ))}
            </div>

            <div className="zp-label mt-10 mb-3 !text-[#5CE1E6]">{t("foot.hq")}</div>
            <div className="text-[#E2EEF3] text-sm">{t("foot.hqValue")}</div>
            <div className="text-[#88A2B2] text-xs mt-1">
              Zopran GmbH · AeroShip Global OÜ · ShipMyParcels.com
            </div>
          </div>
        </div>

        <div className="h-px bg-white/10 my-12" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#88A2B2]">
          <div>© {new Date().getFullYear()} Zopran Global Logistics Group. {t("foot.copy")}</div>
          <div className="flex items-center gap-5 flex-wrap">
            <Link to="/impressum" data-testid="footer-impressum" className="hover:text-white">
              {t("nav.impressum")}
            </Link>
            <span>·</span>
            <span>Germany Customs Expertise</span>
            <span className="hidden sm:inline">·</span>
            <span>DDP Specialists</span>
            <span className="hidden sm:inline">·</span>
            <span>India ↔ Europe Gateway</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
